/* eslint-disable complexity */
import React from 'react';
import Icon from 'carbon-react/lib/components/icon';
import Typography from 'carbon-react/lib/components/typography';
import Link from 'carbon-react/lib/components/link';
import { FormattedMessage } from '@futrli/localisation';
import { FINANCIAL_INTEGRATION } from '@futrli/predict/core/types';

import { useOrganisationIntegration } from '../../../../predict-management/hooks/useOrganisationIntegration';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { SyncContentLoader } from './sync-control/components/sync-content-loader/sync-content-loader';

interface Props {
  unreconciledTransactionsCount?: number | null;
  isLoading: boolean;
  syncFailed: boolean;
}

export const Reconciliation = ({ unreconciledTransactionsCount, isLoading, syncFailed }: Props) => {
  const { integration } = useOrganisationIntegration();

  const { isConsolidation } = usePredictionsInfoContext();

  if (isConsolidation) {
    return null;
  }

  if (syncFailed) {
    return (
      <div style={{ margin: '16px 0' }} data-testid="sync-all-reconciled">
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
        </Typography>
        <div>
          <Icon type="error" color="#CB374A" mr="8px" />
          <FormattedMessage id="datasource.data_sync_failed" defaultMessage="Data sync failed" />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ margin: '16px 0' }} data-testid="sync-loading-bank-transactions">
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
        </Typography>
        <SyncContentLoader width="60%" height={16} />
      </div>
    );
  }

  if (integration?.slug === FINANCIAL_INTEGRATION.sage50) {
    return (
      <div style={{ margin: '16px 0' }} data-testid="sync-all-reconciled">
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
        </Typography>

        <div>
          <FormattedMessage
            id="predict.ensure_all_bank_transactions"
            defaultMessage="Ensure all bank transactions in Sage 50 are reconciled before resync. You're good to go!"
          />
        </div>
      </div>
    );
  }

  if (integration?.slug === FINANCIAL_INTEGRATION.qbo) {
    return (
      <div style={{ margin: '16px 0' }} data-testid="sync-all-reconciled">
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
        </Typography>

        <div>
          <FormattedMessage
            id="predict.we_are_only_able_to_see_bank_reconciled"
            defaultMessage="We’re only able to see bank transactions that have been reconciled. Are your bank recs up to date?"
          />
          &nbsp;
          <FormattedMessage
            id="predict.log_into_qbo"
            defaultMessage="<a>Log into QBO.</a>"
            values={{
              a: (msg: string) => (
                <Link href="https://app.qbo.intuit.com/" target="_blank" rel="noopener noreferrer">
                  {msg}
                </Link>
              )
            }}
          />
        </div>
      </div>
    );
  }

  if (typeof unreconciledTransactionsCount === 'number' && unreconciledTransactionsCount > 0) {
    return (
      <div style={{ margin: '16px 0' }} data-testid="sync-all-reconciled">
        <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
          <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
        </Typography>
        <div>
          <Icon type="error" color="#CB374A" mr="8px" />
          <FormattedMessage
            id="predict.you have_value_unreconciled_bank"
            defaultMessage="You have {unreconciled_transactions_count} unreconciled bank transactions. "
            values={{
              unreconciled_transactions_count: unreconciledTransactionsCount
            }}
          />
          &nbsp;
          {integration?.slug === 'xero' || integration?.slug === 'sage' ? (
            <FormattedMessage
              id="predict.log_into_provider"
              defaultMessage="<a>Log into {provider}</a>."
              values={{
                provider: integration?.slug === 'xero' ? 'Xero' : 'Sage',
                a: (msg: string) => (
                  <Link
                    href={
                      integration?.slug === 'xero'
                        ? 'https://login.xero.com/'
                        : 'https://www.sage.com/en-gb/products/login/'
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {msg}
                  </Link>
                )
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '16px 0' }} data-testid="sync-all-reconciled">
      <Typography variant="p" mb="4px" color="rgba(0, 0, 0, 0.65)">
        <FormattedMessage id="common.bank_transactions" defaultMessage="Bank transactions" />
      </Typography>

      <div>
        <Icon type="tick_circle" color="#00821F" mr="8px" />
        <FormattedMessage
          id="predict.all_bank_reconciled"
          defaultMessage="All bank transactions are reconciled. You’re good to go!"
        />
      </div>
    </div>
  );
};
